import { makeComputationApiRepository } from "../../core/repositories/repositoryBuilders.js";
import AccountingDocumentService from "../shared/documentInfrastructure/AccountingDocumentService.js";
import ArrayStream from "../../core/list/streams/ArrayStream.js";
import Forecast from "../../models/forecast/instance/Forecast.js";
import ListSource from "../../core/list/ListSource.js";

/**
 * Service responsible for all data exchange required by the forecast
 * archive document. The receiver coordinates other services and
 * repositories.
 */
export default class ForecastArchiveDocumentService extends AccountingDocumentService {
	constructor({ session }) {
		super(...arguments);

		this._forecastRepository = makeComputationApiRepository({
			route: "Forecasts/?id",
			model: Forecast,
			session,
		});

		this._forecastEstimateRepository = makeComputationApiRepository({
			route: "Forecasts/?id/Estimate",
			session,
		});
	}

	/**
	 * Request the initial data required by the document.
	 */
	async fetchInitialData() {
		await super.fetchInitialData(...arguments);

		return {
			publishedForecastSource: this._makePublishedForecastSource(),
		};
	}

	deleteForecast(forecast) {
		return this._forecastRepository.remove({
			filter: { id: forecast.getId() },
		});
	}

	async tagForecastAsEstimate(forecast) {
		await this._forecastEstimateRepository.save({
			notify: false,
			filter: { id: forecast.getId() },
		});

		return true;
	}

	async untagForecastAsEstimate(forecast) {
		await this._forecastEstimateRepository.remove({
			notify: false,
			filter: { id: forecast.getId() },
		});

		return false;
	}

	isForecastEstimate(forecast) {
		return this._forecastEstimateRepository.head({
			filter: { id: forecast.getId() },
		});
	}

	_makePublishedForecastSource() {
		return ListSource.makeAndFetch({
			sortFieldAccessor: "CreationDate",
			sortAscending: false,
			groupByFieldAccessor: "CurrentPeriodString",
			stream: new ArrayStream({
				data: () => this._getForecastAndCheckIsEstimate(),
			}),
		});
	}

	async _getForecastAndCheckIsEstimate() {
		const forecasts = await this._forecastRepository.get();

		await Promise.all(
			forecasts.map(async (forecast) => {
				const isEstimate = await this.isForecastEstimate(forecast);
				forecast.setIsEstimate(isEstimate);
			}),
		);

		return forecasts;
	}
}
